/**
 * CLI feature-flag announcement management.
 *
 * Announcements are messages attached to a flag, shown when the flag is on/GA'd.
 */

import process from 'node:process';
import { readFileSync } from 'node:fs';
import { apiRequest, fetchList } from './cli-helpers.js';
import { getArgValue, hasFlag } from './auth.js';

type AnnouncementConfig = {
  uiComponent: string;
  title: string;
  body: string;
  ctaLabel?: string;
  ctaHref?: string;
  dismissLabel?: string;
};

type Announcement = {
  id: string;
  flagName: string;
  uiComponent: string;
  config: AnnouncementConfig;
  seenScope: 'user' | 'org';
  active: boolean;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  viewCount?: number;
};

type AnnouncementApiResponse = {
  ok: boolean;
  error?: string;
  announcement?: Announcement;
};

/** Resolve a `--body @file.md` (read file) or inline string value. */
function readContent(value: string): string {
  if (value.startsWith('@')) {
    return readFileSync(value.slice(1), 'utf8');
  }
  return value;
}

/** Resolve `--ttl 7d|12h|30m` into an absolute ISO expiry, or undefined. */
function parseTtlToIso(ttl: string): string {
  const match = /^(\d+)([dhm])$/.exec(ttl.trim());
  if (!match) {
    console.error('Error: --ttl must look like 7d, 12h, or 30m.');
    process.exit(1);
  }
  const n = Number.parseInt(match[1], 10);
  const unitMs = match[2] === 'd' ? 86_400_000 : match[2] === 'h' ? 3_600_000 : 60_000;
  return new Date(Date.now() + n * unitMs).toISOString();
}

async function handleAnnouncementCreate(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const flag = argv[0];
  if (!flag || flag.startsWith('--')) {
    console.error('Error: Missing flag name.');
    console.error(
      'Usage: canary feature-flag announcement create <flag> --title <t> --body @file|"text" [--scope user|org] [--ttl 7d|--expires <iso>] [--cta-label <l> --cta-href <url>] [--dismiss-label <l>] [--inactive]'
    );
    process.exit(1);
  }

  const title = getArgValue(argv, '--title');
  const rawBody = getArgValue(argv, '--body');
  if (!title || !rawBody) {
    console.error('Error: --title and --body are required.');
    process.exit(1);
  }
  const body = readContent(rawBody);

  const scope = getArgValue(argv, '--scope');
  if (scope !== undefined && scope !== 'user' && scope !== 'org') {
    console.error('Error: --scope must be user or org.');
    process.exit(1);
  }

  const ctaLabel = getArgValue(argv, '--cta-label');
  const ctaHref = getArgValue(argv, '--cta-href');
  if ((ctaLabel && !ctaHref) || (!ctaLabel && ctaHref)) {
    console.error('Error: --cta-label and --cta-href must be provided together.');
    process.exit(1);
  }
  const dismissLabel = getArgValue(argv, '--dismiss-label');
  const component = getArgValue(argv, '--component') ?? 'interstitial_dialog';

  const ttl = getArgValue(argv, '--ttl');
  const expires = getArgValue(argv, '--expires');
  if (ttl && expires) {
    console.error('Error: provide either --ttl or --expires, not both.');
    process.exit(1);
  }
  const expiresAt = ttl ? parseTtlToIso(ttl) : (expires ?? null);

  const config: AnnouncementConfig = {
    uiComponent: component,
    title,
    body,
    ...(ctaLabel && ctaHref ? { ctaLabel, ctaHref } : {}),
    ...(dismissLabel ? { dismissLabel } : {}),
  };

  const result = await apiRequest<AnnouncementApiResponse>(
    apiUrl,
    token,
    'POST',
    '/superadmin/announcements',
    {
      flagName: flag,
      config,
      seenScope: scope,
      active: hasFlag(argv, '--inactive') ? false : undefined,
      expiresAt,
    }
  );

  if (!result.ok || !result.announcement) {
    console.error(`Error: ${result.error ?? 'Failed to create announcement'}`);
    process.exit(1);
  }

  console.log(
    `Created announcement ${result.announcement.id} for ${flag} (scope=${result.announcement.seenScope}${expiresAt ? `, expires ${expiresAt}` : ''})`
  );
}

async function handleAnnouncementList(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const flag = argv[0] && !argv[0].startsWith('--') ? argv[0] : undefined;
  const path = flag
    ? `/superadmin/announcements?flagName=${encodeURIComponent(flag)}`
    : '/superadmin/announcements';
  const announcements = await fetchList<Announcement>(apiUrl, token, path, 'announcements');

  if (jsonOutput) {
    console.log(JSON.stringify(announcements, null, 2));
    return;
  }

  if (announcements.length === 0) {
    console.log('No announcements found.');
    return;
  }

  const now = Date.now();
  for (const a of announcements) {
    const expired = a.expiresAt ? new Date(a.expiresAt).getTime() <= now : false;
    const state = !a.active ? 'inactive' : expired ? 'expired' : 'live';
    const expiry = a.expiresAt ? ` expires=${a.expiresAt}` : '';
    console.log(
      `  ${a.id}  flag=${a.flagName}  [${state}] scope=${a.seenScope} views=${a.viewCount ?? 0}${expiry}`
    );
    console.log(`      ${a.config.title}`);
  }
}

async function handleAnnouncementSetActive(
  argv: string[],
  apiUrl: string,
  token: string,
  active: boolean
): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error(
      `Error: Missing announcement id.\nUsage: canary feature-flag announcement ${active ? 'activate' : 'deactivate'} <id>`
    );
    process.exit(1);
  }
  const result = await apiRequest<AnnouncementApiResponse>(
    apiUrl,
    token,
    'POST',
    `/superadmin/announcements/${encodeURIComponent(id)}/active`,
    { active }
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`${active ? 'Activated' : 'Deactivated'} announcement ${id}`);
}

async function handleAnnouncementExpire(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error(
      'Error: Missing announcement id.\nUsage: canary feature-flag announcement expire <id>'
    );
    process.exit(1);
  }
  const result = await apiRequest<AnnouncementApiResponse>(
    apiUrl,
    token,
    'POST',
    `/superadmin/announcements/${encodeURIComponent(id)}`,
    { expiresAt: new Date().toISOString() }
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`Expired announcement ${id} (no longer shows)`);
}

async function handleAnnouncementViews(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error(
      'Error: Missing announcement id.\nUsage: canary feature-flag announcement views <id>'
    );
    process.exit(1);
  }
  const jsonOutput = hasFlag(argv, '--json');
  const result = await apiRequest<{
    ok: boolean;
    error?: string;
    count?: number;
    views?: Array<{ userId: string; userEmail: string | null; orgId: string; seenAt: string }>;
  }>(apiUrl, token, 'GET', `/superadmin/announcements/${encodeURIComponent(id)}/views`);

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  const views = result.views ?? [];
  console.log(`${result.count ?? views.length} view(s) for ${id}:`);
  for (const v of views) {
    console.log(`  ${v.userEmail ?? v.userId}  org=${v.orgId}  ${v.seenAt}`);
  }
}

async function handleAnnouncementDelete(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error(
      'Error: Missing announcement id.\nUsage: canary feature-flag announcement delete <id>'
    );
    process.exit(1);
  }
  const result = await apiRequest<AnnouncementApiResponse>(
    apiUrl,
    token,
    'DELETE',
    `/superadmin/announcements/${encodeURIComponent(id)}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`Deleted announcement ${id}`);
}

function printAnnouncementHelp(): void {
  console.log(
    [
      'Usage: canary feature-flag announcement <sub-command> [options]',
      '',
      'Attach an announcement (interstitial dialog) to a flag. Shows to users for',
      "whom the flag is turned on or GA'd, until they've seen it or the TTL passes.",
      '',
      'Sub-commands:',
      '  create <flag> --title <t> --body @file|"text" [--scope user|org] [--ttl 7d|--expires <iso>]',
      '                 [--cta-label <l> --cta-href <url>] [--dismiss-label <l>] [--inactive]',
      '  list [<flag>] [--json]        List announcements (optionally for one flag) + view counts',
      '  activate <id>                 Turn the kill-switch on',
      '  deactivate <id>               Turn the kill-switch off (stops showing)',
      '  expire <id>                   Set expiry to now (stops showing, keeps history)',
      '  views <id> [--json]           Show the recorded view log',
      '  delete <id>                   Delete an announcement (and its views)',
      '',
      'Options:',
      '  --scope user|org   Seen de-dup: per-user (default) or per-org (first viewer suppresses for all)',
      '  --ttl 7d|12h|30m   Relative expiry (mutually exclusive with --expires)',
      '  --expires <iso>    Absolute ISO 8601 expiry',
      "  --inactive         Create paused (won't show until activated)",
    ].join('\n')
  );
}

export async function runAnnouncement(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const [sub, ...rest] = argv;
  if (!sub || sub === 'help' || hasFlag(argv, '--help', '-h')) {
    printAnnouncementHelp();
    return;
  }
  switch (sub) {
    case 'create':
      await handleAnnouncementCreate(rest, apiUrl, token);
      break;
    case 'list':
      await handleAnnouncementList(rest, apiUrl, token);
      break;
    case 'activate':
      await handleAnnouncementSetActive(rest, apiUrl, token, true);
      break;
    case 'deactivate':
      await handleAnnouncementSetActive(rest, apiUrl, token, false);
      break;
    case 'expire':
      await handleAnnouncementExpire(rest, apiUrl, token);
      break;
    case 'views':
      await handleAnnouncementViews(rest, apiUrl, token);
      break;
    case 'delete':
      await handleAnnouncementDelete(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown announcement sub-command: ${sub}`);
      printAnnouncementHelp();
      process.exit(1);
  }
}
